"use client";

import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";
import { z } from "zod";
import { cn } from "../../lib/utils";
import { Avatar } from "../Avatar";
import { Chip } from "../Chip";
import { Spinner } from "../Spinner/Spinner";
import { useUiLabels } from "../UiLabels/useUiLabels";
const emailSchema = z.email();

/** A single typeahead suggestion for the email input. */
export interface EmailSuggestion {
  /** The email address committed as a chip when the suggestion is chosen. */
  email: string;
  /** The text shown in the dropdown row (e.g. "Jane Doe <jane@acme.com>"). */
  label: string;
  /** Avatar image URL for the row, if the member has one. */
  avatarUrl?: string | null;
  /** Name used for the avatar's initials/color fallback (defaults to the label). */
  name?: string;
}
export interface EmailInputProps {
  /**
   * The emails to display in the input.
   */
  emails: string[];
  /**
   * The function to call when the emails change.
   */
  onChange: (emails: string[]) => void;
  /**
   * The label to display for the input.
   */
  label: React.ReactNode;
  /**
   * Whether the input is invalid.
   */
  isInvalid: boolean;
  /**
   * The error message to display if the input is invalid.
   */
  errorMessage?: string;
  /**
   * Whether to autofocus the input on mount.
   */
  autoFocus?: boolean;
  /**
   * Placeholder text for the input when no emails are added.
   */
  placeholder?: string;
  /**
   * Typeahead suggestions to show in a dropdown as the user types. Providing
   * this prop (even an empty array) opts the input into typeahead mode; leaving
   * it undefined keeps the plain free-text-only behavior. Suggestions whose
   * email is already added are filtered out automatically.
   */
  suggestions?: EmailSuggestion[];
  /**
   * Called with the raw input text whenever it changes, so a parent can drive a
   * (debounced) suggestions query.
   */
  onInputValueChange?: (value: string) => void;
  /**
   * Whether suggestions are currently loading — shows a spinner in the dropdown.
   */
  isLoadingSuggestions?: boolean;
  /**
   * Which committed emails matched a typeahead suggestion, so their chips can
   * render an avatar + name instead of the raw address. Lift this to the
   * parent (with `onMatchedSuggestionsChange`) when EmailInput can unmount and
   * remount within the same flow (e.g. a multi-step form) — otherwise this
   * metadata is tracked internally and lost on remount even though `emails`
   * persists. Omit both props to keep the metadata purely internal.
   */
  matchedSuggestions?: Record<string, EmailSuggestion>;
  /**
   * Called with the next matched-suggestions map whenever a chip is committed
   * from or removed from a suggestion. Required alongside `matchedSuggestions`
   * for parent-controlled tracking.
   */
  onMatchedSuggestionsChange?: (matched: Record<string, EmailSuggestion>) => void;
}

/**
 * NOTE: We will probably want to extract input styles so that we can use them
 * here as well. It isn't best practice to have these hard coded styles when we
 * have a design system and other components that have the same styling
 */
export const EmailInput = ({
  emails,
  label: labelProp,
  isInvalid,
  errorMessage,
  onChange,
  autoFocus,
  placeholder: placeholderProp,
  suggestions,
  onInputValueChange,
  isLoadingSuggestions = false,
  matchedSuggestions: matchedSuggestionsProp,
  onMatchedSuggestionsChange
}: EmailInputProps) => {
  const uiLabels = useUiLabels();
  const label = labelProp === undefined ? uiLabels.emailInput.label : labelProp;
  const placeholder = placeholderProp ?? uiLabels.emailInput.placeholder;
  const [inputValue, setInputValue] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [internalMatchedSuggestions, setInternalMatchedSuggestions] = useState<Record<string, EmailSuggestion>>({});
  const matchedSuggestions = matchedSuggestionsProp ?? internalMatchedSuggestions;
  const updateMatchedSuggestions = onMatchedSuggestionsChange ?? setInternalMatchedSuggestions;
  const inputRef = useRef<HTMLInputElement>(null);
  const baseId = useId();
  const listboxId = `${baseId}-listbox`;
  const optionId = (index: number) => `${baseId}-option-${index}`;
  const typeaheadEnabled = suggestions !== undefined;
  const filteredSuggestions = useMemo(() => (suggestions ?? []).filter(suggestion => !emails.includes(suggestion.email)), [suggestions, emails]);
  const hasQuery = inputValue.trim().length > 0;
  const showDropdown = typeaheadEnabled && isOpen && hasQuery && (filteredSuggestions.length > 0 || isLoadingSuggestions);
  const activeId = activeIndex >= 0 && activeIndex < filteredSuggestions.length ? optionId(activeIndex) : undefined;

  // Autofocus doesn't behave correctly for native inputs
  useEffect(() => {
    if (!autoFocus) return;
    const frame = requestAnimationFrame(() => {
      inputRef.current?.focus();
    });
    return () => cancelAnimationFrame(frame);
  }, [autoFocus]);
  const isValidEmail = useCallback((email: string) => {
    try {
      emailSchema.parse(email);
      return true;
    } catch {
      return false;
    }
  }, []);
  const hasInvalidEmails = useMemo(() => {
    return emails.some(email_0 => !isValidEmail(email_0));
  }, [emails, isValidEmail]);

  // Add a single email chip (if new) and reset the input + dropdown state.
  // `suggestion` is passed when committing a picked typeahead result, so the
  // chip can later render the member's avatar + name instead of the raw email.
  const commitEmail = (email_1: string, suggestion_0?: EmailSuggestion) => {
    const trimmed = email_1.trim();
    if (trimmed && !emails.includes(trimmed)) {
      onChange([...emails, trimmed]);
      if (suggestion_0) {
        updateMatchedSuggestions({
          ...matchedSuggestions,
          [trimmed]: suggestion_0
        });
      }
    }
    setInputValue("");
    onInputValueChange?.("");
    setActiveIndex(-1);
    setIsOpen(false);
  };
  const handleInputChange = (value: string) => {
    setInputValue(value);
    onInputValueChange?.(value);
    if (typeaheadEnabled) {
      setIsOpen(true);
      setActiveIndex(-1);
    }

    // Auto-add email when user types comma, semicolon, or tab
    const separators = /[,;\t]+/;
    if (separators.test(value)) {
      const emailsToAdd = value.split(separators).map(email_2 => email_2.trim()).filter(email_3 => email_3.length > 0 && !emails.includes(email_3));
      if (emailsToAdd.length > 0) {
        onChange([...emails, ...emailsToAdd]);
        setInputValue("");
        onInputValueChange?.("");
      }
    }
  };
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Suggestion navigation takes precedence over free-text commit.
    if (showDropdown && filteredSuggestions.length > 0) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActiveIndex(index_0 => (index_0 + 1) % filteredSuggestions.length);
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setActiveIndex(index_1 => index_1 <= 0 ? filteredSuggestions.length - 1 : index_1 - 1);
        return;
      }
      if (e.key === "Enter" && activeIndex >= 0 && activeIndex < filteredSuggestions.length) {
        e.preventDefault();
        commitEmail(filteredSuggestions[activeIndex].email, filteredSuggestions[activeIndex]);
        return;
      }
      // While navigating suggestions, let Space refine the search instead of
      // committing the partial input as a chip (and discarding the highlight).
      if (e.key === " ") {
        return;
      }
    }
    if (e.key === "Escape" && isOpen) {
      setIsOpen(false);
      setActiveIndex(-1);
      return;
    }
    if (e.key === "Enter" || e.key === "Tab" || e.key === " ") {
      const trimmedValue = inputValue.trim();
      if (trimmedValue && !emails.includes(trimmedValue)) {
        e.preventDefault();
        commitEmail(trimmedValue);
      } else if (e.key === "Tab" && !trimmedValue) {
        // Allow tab to move focus if input is empty
        return;
      } else {
        e.preventDefault();
      }
    } else if (e.key === "Backspace" && !inputValue && emails.length > 0) {
      // Remove last email when backspace is pressed on empty input
      e.preventDefault();
      removeEmail(emails.length - 1);
    }
  };
  const handleBlur = () => {
    setIsOpen(false);
    setActiveIndex(-1);
    const trimmedValue_0 = inputValue.trim();
    if (trimmedValue_0 && !emails.includes(trimmedValue_0)) {
      onChange([...emails, trimmedValue_0]);
      setInputValue("");
      onInputValueChange?.("");
    }
  };
  const removeEmail = (indexToRemove: number) => {
    const removed = emails[indexToRemove];
    onChange(emails.filter((_, index_2) => index_2 !== indexToRemove));
    if (removed && removed in matchedSuggestions) {
      const {
        [removed]: _removed,
        ...rest
      } = matchedSuggestions;
      updateMatchedSuggestions(rest);
    }
  };
  const hasValidationErrors = isInvalid || hasInvalidEmails;

  // Shared between the plain and combobox input variants. Rendering two variants
  // (rather than one input with conditional ARIA) keeps the combobox ARIA props
  // statically attached to a role="combobox" element, which is required for them
  // to be valid.
  const sharedInputProps = {
    id: "emails",
    ref: inputRef,
    type: "text" as const,
    value: inputValue,
    onChange: (e_0: React.ChangeEvent<HTMLInputElement>) => handleInputChange(e_0.target.value),
    onKeyDown: handleKeyDown,
    onBlur: handleBlur,
    placeholder: emails.length === 0 ? placeholder : "",
    className: cn("min-h-[1.75rem] min-w-[10rem] flex-1",
    // Minimum width but grows to fill row
    "bg-transparent outline-none", "text-foreground text-sm placeholder:text-text-interactive-neutral-faint")
  };
  return <div className="space-y-2">
      <label htmlFor="emails" className={cn("block font-medium text-foreground text-sm", isInvalid ? "text-danger" : "text-foreground")}>
        {label}
      </label>
      <div className="space-y-1">
        {/* Relative anchor so the suggestion dropdown positions against the field only. */}
        <div className="relative">
          {/* Custom input container: Chips and input are siblings in the same flex container
              This allows the input cursor to fill available space on the same row as chips */}
          <div className={cn("flex flex-wrap content-start items-start gap-1.5", "overflow-y-auto",
        // Fixed height with scroll
        "h-28", "px-3 py-2", "rounded-medium border-1 border-border-interactive-neutral-default focus-within:border-border-interactive-neutral-selected! hover:border-border-interactive-neutral-hover", "transition-colors", hasValidationErrors && "border-danger focus-within:border-danger!")}>
            {emails.map((email_4, index_3) => {
            const isValid = isValidEmail(email_4);
            const match = matchedSuggestions[email_4];
            return <Chip key={email_4}
            // Matches the token chips in MultiSelectCombobox — the two sit in near-identical
            // fields, and a solid chip here read as a different kind of control.
            variant="flat" size="sm" color={isValid ? "default" : "danger"} className="min-w-0 max-w-full" startContent={match ? <Avatar src={match.avatarUrl ?? undefined} name={match.name ?? match.label} size="sm" className="size-5 shrink-0" /> : undefined}
            // Chip's own close, as MultiSelectCombobox uses — a hand-rolled one carried its
            // own icon and spacing, which is what set the two fields' chips apart.
            onClose={() => removeEmail(index_3)} aria-label={match?.name ?? email_4}>
                  <span className="block min-w-0 truncate">{match ? match.name ?? match.label : email_4}</span>
                </Chip>;
          })}
            {/* Native input element positioned as a flex sibling to chips
              min-w ensures it doesn't shrink too small, flex-1 makes it fill available space */}
            {typeaheadEnabled ? <input {...sharedInputProps} role="combobox" aria-expanded={showDropdown} aria-controls={listboxId} aria-autocomplete="list" aria-activedescendant={activeId} /> : <input {...sharedInputProps} />}
          </div>
          {showDropdown ? <div id={listboxId} role="listbox" aria-label={uiLabels.emailInput.suggestions} className="absolute inset-x-0 top-[calc(100%+0.25rem)] z-50 max-h-56 overflow-auto rounded-medium border border-default-200 bg-content1 p-1 shadow-medium">
              {isLoadingSuggestions ? <div className="flex items-center justify-center gap-2 px-2 py-3 text-default-500 text-sm">
                  <Spinner size="sm" /> {uiLabels.emailInput.loading}
                </div> : filteredSuggestions.map((suggestion_1, index_4) => <button key={suggestion_1.email} type="button" id={optionId(index_4)} role="option" aria-selected={activeIndex === index_4} data-active={activeIndex === index_4} className={cn("flex w-full min-w-0 items-center gap-2 rounded-small px-2 py-1.5 text-left text-foreground text-sm hover:bg-default-100", activeIndex === index_4 && "bg-default-100")}
          // Commit on mousedown-prevented click so the input keeps focus (blur doesn't fire first).
          onMouseDown={event => event.preventDefault()} onMouseEnter={() => setActiveIndex(index_4)} onClick={() => commitEmail(suggestion_1.email, suggestion_1)}>
                    {/* Decorative: the option's accessible name stays the label text. */}
                    <span aria-hidden="true" className="shrink-0">
                      <Avatar src={suggestion_1.avatarUrl ?? undefined} name={suggestion_1.name ?? suggestion_1.label} className="h-6 w-6 text-tiny" />
                    </span>
                    <span className="block min-w-0 truncate">{suggestion_1.label}</span>
                  </button>)}
            </div> : null}
        </div>
        {(isInvalid || errorMessage) && <p className={cn("mt-1 text-danger text-xs")}>
            {errorMessage || (hasInvalidEmails ? uiLabels.emailInput.invalid : "")}
          </p>}
      </div>
    </div>;
};